import { SeverityBadge } from './SeverityBadge.jsx';
import { severityClass } from '../utils/severity.js';
import { sentenceCase } from '../../pdf/helpers/check-name-case.js';

function actionText(action) {
  if (!action) return null;
  if (typeof action === 'string') return action;
  return action.statement || action.text || action.message || null;
}

/**
 * A single row inside the Fix Queue panel. Shows rank, severity badge,
 * check name, affected count, engine-provided corrective action statement,
 * and up to three example activities. Emits NO fabricated figures —
 * no "hours saved", no ROI, no dollar signs (Data Integrity Rule).
 */
export function FixQueueRow({ issue }) {
  if (!issue) return null;
  // Panel label uses `severityLabel` (threshold + high-impact category) per
  // Amendment §A4. Per-finding `issue.severity` does NOT drive panel labels.
  const sevLabel = issue.severityLabel || issue.severity;
  const cls = severityClass(sevLabel);
  const count = issue.affectedCount;
  const action = actionText(issue.correctiveAction);
  const examples = Array.isArray(issue.exampleActivities)
    ? issue.exampleActivities.slice(0, 3)
    : [];

  return (
    <article className={`fix-row fix-row-${cls}`}>
      <header className="fix-row-head">
        <span className="fix-rank">#{issue.rank}</span>
        <SeverityBadge severity={sevLabel} />
        {/* X-1 (V-18): render the shared sentence-case display of the canonical
            Title-Case check_name — byte-identical to CheckBlock's check title and
            the PDF's sentenceCase(checkName) (check-name-case.js is the one
            implementation). The Fix Queue was the last title-bearing surface
            still showing raw Title Case (D-DashTitleCase's untouched twin). */}
        <h3 className="fix-title">
          Check #{issue.checkNumber} — {sentenceCase(issue.checkName)}
        </h3>
        {typeof count === 'number' && (
          <span className="fix-affected">
            {count} {count === 1 ? 'activity' : 'activities'} affected
          </span>
        )}
      </header>

      {issue.explanation && (
        <p className="fix-explanation">{issue.explanation}</p>
      )}

      {action && (
        <div className="fix-action">
          <div className="fix-action-title">Corrective Action</div>
          <p className="fix-action-text">{action}</p>
        </div>
      )}

      {examples.length > 0 && (
        <div className="fix-examples">
          <div className="fix-examples-title">Example activities</div>
          <ul className="fix-examples-list">
            {examples.map((ex) => (
              <li key={ex.activityId}>
                <span className="fix-ex-id">{ex.activityId}</span>
                <span className="fix-ex-name">{ex.activityName}</span>
                {ex.wbsPath && (
                  <span className="fix-ex-wbs">{ex.wbsPath}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}
